import { Button, Drawer, Flex, Modal, Tooltip } from 'antd';
import { LBNodeData } from '@/proto/logiblocks/logiblocks';
import styles from './CustomizeLBNodePanel.module.css';
import sidePanelStyles from './SidePanel.module.css';
import {
  getLBNodeInfo,
  getNodeDisplayName,
  requireLBNodeInfo,
} from '@/pages/Logiblocks/nodes/registry';
import {
  CATEGORY_ICON_BG,
  type GraphData,
  NodeCategory,
  type PanelScreen,
} from '@/pages/Logiblocks/types';
import type { FilterControlFlowField } from '@/apis/logiblocksAPI';
import { DeprecatedValidationErrorBanner } from './DeprecatedValidationErrorBanner';
import { validateUserVariables } from './validation';
import { useHoldWhileClosing } from './useHoldWhileClosing';
import {
  CircleInfoIcon,
  CloseDrawerIcon,
  DeleteIcon,
  LeftTailArrowIcon,
  PencilIcon,
} from '@/assets/icons/icons';
import InlineInput from '@/components/InlineInput';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CustomShipmentText } from '@/components';
import { bytesEqual, lbNodeDataToBytes } from './serialization';
import CommanFooter from '@/components/CommonFooter';
import { usePageScrollLock } from '@/hooks/usePageScrollLock';

/**
 * Maps node category to display label
 */
function getCategoryLabel(category: NodeCategory): string {
  switch (category) {
    case NodeCategory.TRIGGER:
      return 'Trigger';
    case NodeCategory.ACTION:
      return 'Action';
    case NodeCategory.CONTROL_FLOW:
      return 'Flow control';
    case NodeCategory.NOTIFICATIONS:
      return 'Notification';
    default:
      return 'Node';
  }
}

/**
 * Props for CustomizeLBNodePanel.
 *
 * Edits are held in a draft inside the panel; nothing commits to the graph
 * until the user hits Save, which fires `onSaveAndClose(draft)`.
 *
 * Two close paths on the parent side:
 *   - `onSaveAndClose`     — Save button, or the unsaved-changes modal's Save
 *                            option
 *   - `onCloseWithoutSave` — Cancel button, X button (when the draft is clean),
 *                            or the unsaved-changes modal's "Don't Save"
 *
 * Escape/mask/close-icon on the unsaved-changes modal only dismisses the
 * modal; the panel stays open with the draft intact.
 */
interface CustomizeLBNodePanelProps {
  /** Whether the panel is visible */
  open: boolean;

  /** Called with the final draft when the user elects to save. */
  onSaveAndClose: (data: LBNodeData) => void;

  /** Called when the panel should close without committing the draft. */
  onCloseWithoutSave: () => void;

  /** Called when the currently edited node should be deleted from the graph. */
  onDeleteNode?: (nodeId: string) => void;

  /** The ID of the node we're editing */
  nodeId: string;

  /**
   * The node's data at the time the panel opened. The panel seeds its internal
   * draft from this value and tracks edits locally.
   */
  initialNodeData: Readonly<LBNodeData>;

  /** Whether the panel is read-only (published workflow). Defaults to false. */
  isReadOnly?: boolean;

  /**
   * The full graph. Used for live validation of the current draft: the panel
   * passes this to `nodeInfo.getValidationErrors` along with `draftNodeData`,
   * so errors update as the user edits.
   */
  graph: GraphData;

  /** ID of the parent workflow – forwarded to customisation components for label pre-fetching */
  workflowId: string;

  /**
   * Fields pre-fetched for filterControlFlow panels. Conditional panels fetch
   * lazily inside ConditionalCustomization and do not use this prop.
   */
  prefetchedFields?: FilterControlFlowField[];

  /**
   * Parent node ID resolved at panel-open time. Forwarded to customisation
   * components that need to make paginated API calls (e.g. filterControlFlow).
   */
  prefetchedFieldsParentId?: string;
}

/**
 * Imperative handle so a parent switching to a different node's panel can
 * run this panel's own dirty-draft check first, instead of just unmounting
 * it (the parent remounts via key={nodeId}, which would otherwise silently
 * discard an in-progress edit with no warning).
 */
export interface CustomizeLBNodePanelHandle {
  /**
   * Requests that this panel close so the parent can switch to a different
   * node. If the draft is clean, calls onProceed immediately. If dirty,
   * shows the same "Save changes?" modal the X button uses; onProceed only
   * fires once the user resolves it via Save or Don't Save (never on a bare
   * dismiss, and not if Save fails validation).
   */
  requestClose: (onProceed: () => void) => void;
}

/**
 * Header of a full-panel screen: a back arrow, a close button and the screen's title.
 */
const PanelScreenHeader = ({
  title,
  backLabel,
  onBack,
  onClose,
}: {
  title: string;
  backLabel?: string;
  onBack: () => void;
  onClose: () => void;
}) => {
  return (
    <>
      <div className={styles.customizeAlertHeader}>
        <Flex justify='space-between' className={styles.panelScreenTopRow}>
          <button onClick={onBack} className={styles.backButton}>
            <span className={styles.backIcon}>
              <LeftTailArrowIcon size={16} color='var(--text-primary)' />
            </span>
            {backLabel && <span className={styles.backLabel}>{backLabel}</span>}
          </button>
          <button
            onClick={onClose}
            className={`${sidePanelStyles.closeButton} ${styles.panelScreenClose}`}
            aria-label='Close'
          >
            <CloseDrawerIcon />
          </button>
        </Flex>
        <CustomShipmentText
          text={title}
          fontWeight={700}
          fontSize='24px'
          lineHeight='32px'
          className={styles.panelScreenTitle}
        />
      </div>
    </>
  );
};

/**
 * Custom header component for the customization panel
 */
const CustomizeHeader = ({
  nodeData,
  onChange,
  sequenceNumber,
}: {
  // Readonly: edits commit via onChange, not by mutating nodeData.
  nodeData: Readonly<LBNodeData>;
  onChange: (updatedData: LBNodeData) => void;
  sequenceNumber: number;
}) => {
  const nodeInfo = getLBNodeInfo(nodeData);
  const currentName = nodeInfo ? getNodeDisplayName(nodeData, nodeInfo) : '';
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(currentName);

  if (!nodeInfo) {
    return null;
  }

  const handleStartRename = () => {
    setIsRenaming(true);
    setNameDraft(currentName);
  };

  const handleRenameSave = () => {
    const trimmed = nameDraft.trim();
    if (trimmed && trimmed !== currentName) {
      onChange({ ...nodeData, custom_display_name: trimmed });
    }
    setIsRenaming(false);
  };

  const handleRenameCancel = () => {
    setIsRenaming(false);
    setNameDraft(currentName);
  };

  return (
    <div className={styles.customizeHeader}>
      <span className={styles.sequenceNumber}>{sequenceNumber}.</span>
      <div
        className={`lb-icon-wrapper ${styles.nodeIcon}`}
        // eslint-disable-next-line react/forbid-dom-props -- The icon color comes from the node's category at runtime.
        style={{ backgroundColor: CATEGORY_ICON_BG[nodeInfo.category] }}
      >
        {nodeInfo.getIcon(24)}
      </div>
      <div className={styles.headerInfo}>
        <span className={styles.categoryLabel}>
          {getCategoryLabel(nodeInfo.category)}
        </span>
        {isRenaming ? (
          <div className={styles.renameRow}>
            <InlineInput
              value={nameDraft}
              placeholder='Enter title...'
              onChange={setNameDraft}
              onAdd={handleRenameSave}
              onCancel={handleRenameCancel}
              autoFocus={true}
            />
          </div>
        ) : (
          <Flex align='center' gap={4} className={styles.nodeName}>
            {currentName}
            <div
              className={styles.editTitleButton}
              onClick={handleStartRename}
              data-testid='edit-node-title'
            >
              <PencilIcon color='var(--text-primary)' />
            </div>
          </Flex>
        )}
      </div>
    </div>
  );
};

/**
 * Generic customization panel for Logiblocks nodes.
 * Renders node-specific customization UI based on node type.
 *
 * Edits live in a local draft and do not reach the graph until the user hits
 * Save. X button prompts on unsaved changes; Cancel always discards.
 */
export const CustomizeLBNodePanel = forwardRef<
  CustomizeLBNodePanelHandle,
  CustomizeLBNodePanelProps
>(function CustomizeLBNodePanel(
  {
    open,
    onSaveAndClose,
    onCloseWithoutSave,
    onDeleteNode,
    nodeId,
    initialNodeData,
    isReadOnly = false,
    graph,
    workflowId,
    prefetchedFields,
    prefetchedFieldsParentId,
  }: CustomizeLBNodePanelProps,
  ref
) {
  // The draft the user is editing. Seeded from initialNodeData at mount;
  // never replaced by prop changes — the parent remounts the panel (via
  // key={nodeId}) when switching to a different node.
  const [draftNodeData, setDraftNodeData] =
    useState<LBNodeData>(initialNodeData);
  const initialBytesRef = useRef<Uint8Array>(
    lbNodeDataToBytes(initialNodeData)
  );

  // The Drawer runs without antd's mask (so the canvas stays usable), which
  // also skips antd's page scroll lock - without this, scrolling inside the
  // panel (or over a panel too short to scroll) scrolls the page behind it.
  usePageScrollLock(open);

  // Guards against a rapid double-click on Save firing onSaveAndClose twice.
  const savingRef = useRef(false);

  // Set by requestClose (via the imperative handle) when a parent wants to
  // switch to a different node's panel. Only invoked once the dirty-draft
  // prompt below (if any) actually resolves in a way that lets the panel
  // close -- see handleUnsavedChangesSave/Discard/Dismiss.
  const pendingProceedRef = useRef<(() => void) | null>(null);

  // Whether the "unsaved changes" confirm modal is showing.
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] =
    useState(false);

  // Errors on an already-configured node (the same ones that painted it red
  // on the canvas) show immediately - hiding them just reintroduces the "why
  // is this red" mystery clicking into the node was supposed to answer. A
  // brand-new, never-configured node is exempt: it's always invalid (empty
  // required fields) the instant it's created, and painting it red before
  // the author has touched anything would be a false alarm, not a real one.
  // A freshly-introduced error from further edits still waits for the first
  // Save attempt before painting the panel red. Once flipped, the flag stays
  // on so subsequent edits re-validate live and fields "heal" in place
  // without another Save click.
  const [hasAttemptedSave, setHasAttemptedSave] = useState(() => {
    const initialNodeInfo = requireLBNodeInfo(initialNodeData);
    const isUnconfiguredDefault = bytesEqual(
      lbNodeDataToBytes(initialNodeData),
      lbNodeDataToBytes(initialNodeInfo.createDefaultNodeData())
    );
    if (isUnconfiguredDefault) return false;
    const initialErrors = [
      ...(initialNodeInfo.getValidationErrors?.(
        nodeId,
        initialNodeData,
        graph
      ) ?? []),
      ...validateUserVariables(initialNodeData),
    ];
    return initialErrors.length > 0;
  });

  const isDirty = useMemo(
    () =>
      !bytesEqual(lbNodeDataToBytes(draftNodeData), initialBytesRef.current),
    [draftNodeData]
  );

  const nodeInfo = requireLBNodeInfo(draftNodeData);
  const CustomizationComponent = nodeInfo.CustomizationComponent;
  const drawerWidth =
    nodeInfo.protobufNodeDataKey === 'conditional' ? 540 : 400;
  const sequenceNumber = useMemo(() => {
    const index = graph.nodes.findIndex((node) => node.id === nodeId);
    return index >= 0 ? index + 1 : 1;
  }, [graph.nodes, nodeId]);
  const [step, setStep] = useState(2);
  const [panelScreen, setPanelScreen] = useState<PanelScreen | null>(null);
  // The panel stays as it was while its drawer slides out, so a screen is dropped only once the
  // panel is gone, not when it closes. A screen belongs to the node and panel session that opened
  // it.
  const stillShown = useHoldWhileClosing(open);
  useEffect(() => {
    setPanelScreen(null);
  }, [nodeId, stillShown]);
  const childAddSelectedTypesRef = useRef<(() => void) | null>(null);

  // Errors for the current draft, recomputed on every edit. Mirrors
  // computeValidationErrors (validation.ts) - the node-type hook plus the
  // generic user_variables check that every node type is subject to,
  // regardless of whether its own hook happens to include it.
  const liveValidationErrors = useMemo(
    () => [
      ...(nodeInfo.getValidationErrors?.(nodeId, draftNodeData, graph) ?? []),
      ...validateUserVariables(draftNodeData),
    ],
    [nodeInfo, nodeId, draftNodeData, graph]
  );

  // Errors actually surfaced to the UI. Empty until the user clicks Save for the first time.
  const revealedValidationErrors = hasAttemptedSave ? liveValidationErrors : [];

  // Shallow-merges partial updates so children emitting their own slice
  // don't wipe unrelated top-level keys (e.g. custom_display_name).
  const handleDraftChange = useCallback((updates: Partial<LBNodeData>) => {
    setDraftNodeData((prev) => ({ ...prev, ...updates }));
  }, []);

  // Commit the draft upstream — but only if it passes validation. If there
  // are errors, flip the reveal flag so they show and leave the panel open;
  // the user must fix them (or Cancel) before Save can close. Guarded so a
  // quick second click is a no-op. Returns whether the save actually went
  // through, so callers (e.g. the unsaved-changes modal) know whether it's
  // safe to proceed with whatever prompted the save.
  const handleSave = (): boolean => {
    if (savingRef.current) {
      return false;
    }
    if (liveValidationErrors.length > 0) {
      setHasAttemptedSave(true);
      return false;
    }
    savingRef.current = true;
    onSaveAndClose(draftNodeData);
    return true;
  };

  useImperativeHandle(
    ref,
    () => ({
      requestClose: (onProceed) => {
        if (!isDirty) {
          onProceed();
          return;
        }
        pendingProceedRef.current = onProceed;
        if (liveValidationErrors.length > 0) {
          setHasAttemptedSave(true);
        }
        setIsUnsavedChangesModalOpen(true);
      },
    }),
    [isDirty, liveValidationErrors]
  );

  // X button: prompt if there are unsaved changes, otherwise close cleanly.
  // If the draft is also invalid, reveal inline errors now so they stay
  // visible once the modal dismisses — the user needs to see what's broken.
  const handleAttemptClose = () => {
    if (!isDirty) {
      onCloseWithoutSave();
      return;
    }
    if (liveValidationErrors.length > 0) {
      setHasAttemptedSave(true);
    }
    setIsUnsavedChangesModalOpen(true);
  };

  // "Save" in the unsaved-changes modal: commit the draft and close the
  // panel. If a switch-to-a-different-node request is pending (requestClose
  // via the imperative handle) it only proceeds once the save actually
  // succeeds -- if validation fails, handleSave leaves the panel open to
  // show the errors, same as a bare X-close, and the switch is dropped.
  const handleUnsavedChangesSave = () => {
    setIsUnsavedChangesModalOpen(false);
    const saved = handleSave();
    if (saved) {
      pendingProceedRef.current?.();
    }
    pendingProceedRef.current = null;
  };

  // "Don't Save" in the unsaved-changes modal: discard and close the panel.
  const handleUnsavedChangesDiscard = () => {
    setIsUnsavedChangesModalOpen(false);
    onCloseWithoutSave();
    pendingProceedRef.current?.();
    pendingProceedRef.current = null;
  };

  // X / mask / escape on the unsaved-changes modal: dismiss the modal only.
  // The panel stays open with the draft intact — the user is undoing their
  // accidental click on the panel's X (or abandoning the attempt to switch
  // to a different node).
  const handleUnsavedChangesDismiss = () => {
    setIsUnsavedChangesModalOpen(false);
    pendingProceedRef.current = null;
  };

  const handleDeleteCurrentNode = () => {
    if (isReadOnly || !onDeleteNode) return;
    onDeleteNode(nodeId);
  };

  // Handle going back from alert selection
  const handleBackToMain = () => {
    setStep(2);
  };

  // Handle adding selected alert types - call child's function
  const handleAddSelectedTypes = () => {
    // Call the child component's handleAddSelectedTypes function
    if (childAddSelectedTypesRef?.current) {
      childAddSelectedTypesRef.current();
    }
    // onAddSelectedTypes();
  };

  // Render node-specific customization UI
  const renderCustomizationContent = () => {
    if (!CustomizationComponent) {
      console.error(
        `Customization UI not implemented for node type: ${nodeInfo.label}`
      );
      return (
        <div className={styles.errorMessage}>
          Customization not yet available for this node type.
        </div>
      );
    }

    return (
      <CustomizationComponent
        nodeId={nodeId}
        nodeData={draftNodeData}
        onChange={handleDraftChange}
        isReadOnly={isReadOnly}
        isSidePannelOpen={stillShown}
        step={step}
        setStep={setStep}
        setPanelScreen={setPanelScreen}
        onAddSelectedTypes={handleAddSelectedTypes}
        childAddSelectedTypesRef={childAddSelectedTypesRef}
        workflowId={workflowId}
        validationErrors={revealedValidationErrors}
        prefetchedFields={prefetchedFields}
        prefetchedFieldsParentId={prefetchedFieldsParentId}
      />
    );
  };

  // Footer with Save/Cancel buttons (hidden in read-only mode)
  const footer = isReadOnly ? null : panelScreen ? (
    <CommanFooter
      text={panelScreen.primaryLabel}
      hidePrimary={panelScreen.primaryLabel === undefined}
      plainCancel
      handleCancel={panelScreen.onBack}
      onSave={panelScreen.onPrimary}
    />
  ) : (
    <CommanFooter
      text={step === 3 ? 'Add Selected Types' : 'Save'}
      handleCancel={step === 3 ? handleBackToMain : onCloseWithoutSave}
      onSave={step === 3 ? handleAddSelectedTypes : handleSave}
    />
  );

  // Fallback banner: only rendered when the child customization component
  // hasn't opted into its own inline per-field error UI.
  const bannerError =
    CustomizationComponent?.showDeprecatedValidationErrorBanner
      ? revealedValidationErrors[0]
      : undefined;

  const drawerTitle =
    panelScreen || step === 3 ? (
      <PanelScreenHeader
        title={panelScreen?.title ?? 'Add alert type'}
        backLabel={panelScreen?.backLabel}
        onBack={panelScreen?.onBack ?? handleBackToMain}
        onClose={handleAttemptClose}
      />
    ) : (
      <div className={sidePanelStyles.closeButtonRow}>
        {nodeInfo.protobufNodeDataKey === 'conditional' && (
          <Tooltip
            title={
              <ul className={styles.operatorHelpList}>
                <li>
                  And has priority over Or when multiple operators are chained
                </li>
                <li>
                  Use groups to override priority (groups are evaluated first)
                </li>
              </ul>
            }
            styles={{ root: { maxWidth: 'none' } }}
            placement='left'
          >
            <button
              type='button'
              aria-label='Show conditional operator help'
              className={styles.operatorHelpButton}
            >
              <CircleInfoIcon size={15} />
            </button>
          </Tooltip>
        )}
        {!isReadOnly && onDeleteNode && (
          <button
            type='button'
            onClick={handleDeleteCurrentNode}
            className={sidePanelStyles.closeButton}
            aria-label='Delete node'
            title='Delete node'
          >
            <DeleteIcon />
          </button>
        )}
        <button
          onClick={handleAttemptClose}
          className={sidePanelStyles.closeButton}
          aria-label='Close'
        >
          <CloseDrawerIcon />
        </button>
      </div>
    );

  return (
    <>
      <Drawer
        title={drawerTitle}
        placement='right'
        width={drawerWidth}
        open={open}
        onClose={handleAttemptClose}
        closable={false}
        mask={false}
        className={sidePanelStyles.drawer}
        styles={{
          body: { padding: 0 },
          header: {
            border: 'none',
            borderBottom: 'none',
            padding: '16px 16px 0px',
          },
          footer: { border: 'none', borderTop: 'none', padding: 24 },
        }}
        footer={footer}
        data-testid='customize-panel'
      >
        {step !== 3 && !panelScreen && (
          <>
            <div className={styles.nodeHeader}>
              <CustomizeHeader
                nodeData={draftNodeData}
                onChange={handleDraftChange}
                sequenceNumber={sequenceNumber}
              />
              {bannerError && (
                <DeprecatedValidationErrorBanner
                  error={bannerError}
                  data-testid='panel-validation-error'
                />
              )}
            </div>
            <div className={sidePanelStyles.contentDivider} />
          </>
        )}
        <div className={styles.content}>
          <div data-testid='panel-node-type' className={styles.hidden}>
            {nodeInfo.label}
          </div>
          {renderCustomizationContent()}
        </div>
      </Drawer>
      <Modal
        open={isUnsavedChangesModalOpen}
        onCancel={handleUnsavedChangesDismiss}
        closable={true}
        closeIcon={<CloseDrawerIcon />}
        centered
        width={542}
        className={styles.unsavedChangesModal}
        transitionName='ant-fade'
        maskTransitionName='ant-fade'
        title={
          <span className={styles.unsavedChangesTitle}>
            {liveValidationErrors.length > 0
              ? 'You have unsaved changes'
              : 'Save changes?'}
          </span>
        }
        footer={
          liveValidationErrors.length > 0
            ? [
                <Button
                  key='dont-save'
                  className={styles.unsavedChangesDontSaveButton}
                  onClick={handleUnsavedChangesDiscard}
                >
                  Don&apos;t Save
                </Button>,
                <Button
                  key='fix'
                  type='primary'
                  autoFocus
                  className={styles.unsavedChangesSaveButton}
                  onClick={handleUnsavedChangesDismiss}
                >
                  Fix
                </Button>,
              ]
            : [
                <Button
                  key='dont-save'
                  className={styles.unsavedChangesDontSaveButton}
                  onClick={handleUnsavedChangesDiscard}
                >
                  Don&apos;t Save
                </Button>,
                <Button
                  key='save'
                  type='primary'
                  autoFocus
                  className={styles.unsavedChangesSaveButton}
                  onClick={handleUnsavedChangesSave}
                >
                  Save
                </Button>,
              ]
        }
      >
        <span className={styles.unsavedChangesBody}>
          {liveValidationErrors.length > 0
            ? 'Fill all required fields before saving'
            : 'You have unsaved changes. Do you want to save them?'}
        </span>
      </Modal>
    </>
  );
});
